// react
import { ReactNode, useState } from "react"

// Translations
import { useTrans } from "@/i18n"

// UI
import { classNames } from "@/lib/ui"
import { Button } from "@/components/Button"
import { Heading } from "@/components/Typography"

// Icons
import { FiCopy } from "react-icons/fi"

// Copy clipboard
import { copy } from "@/lib/copyClipboard"

interface ErrorStateProps {
	title?: string
	message?: string
	log?: string
	className?: string
}

/**
 * ErrorState
 * @param param0
 * @returns
 */
export const ErrorState = ({
	title,
	message,
	log,
	className,
}: ErrorStateProps) => {
	const t = useTrans("common")

	// State
	const [showLog, setShowLog] = useState(false)

	// When copy error log
	const onCopy = async (log: string) => {
		setShowLog(true)
		try {
			await copy(log)
		} catch {}
	}

	return (
		<ErrorStateCard className={className}>
			<Heading as="h3" styleAs="h5" className="sm:mt-2">
				{title || t("common.error_state.title")}
			</Heading>
			<p className="m-auto mt-4 max-w-sm text-sm text-gray-600">
				{message || t("common.error_state.text")}
			</p>

			{/* Retry button */}
			<Button
				onClick={() => {
					// This forces reload of whole page. We do this to also invalidate any caches!
					window.location.reload()
				}}
				variant="primary"
				className="mt-8 sm:mt-6"
			>
				{t("common.error_state.retry")}
			</Button>

			{/* Log */}
			{log && (
				<div className="mt-4">
					{showLog ? (
						<p className="m-auto mt-4 h-32 max-w-sm overflow-y-auto rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
							{log}
						</p>
					) : (
						<Button
							variant="transparent"
							size="small"
							onClick={() => void onCopy(log)}
						>
							<FiCopy className="mr-2" />
							{t("common:common.app.error.log")}
						</Button>
					)}
				</div>
			)}
		</ErrorStateCard>
	)
}

interface ErrorStateCardProps {
	children: ReactNode
	className?: string
}

export const ErrorStateCard = ({
	children,
	className,
}: ErrorStateCardProps) => (
	<div
		className={classNames(
			className,
			"overflow-hidden rounded-lg bg-white px-4 py-6 text-center shadow sm:px-6 sm:py-8 lg:px-8 lg:py-10",
		)}
	>
		{children}
	</div>
)
